import React from 'react'

import { formatAmountIndian } from 'app/utils'

import * as Styles from '../styles'

const FUTURE_INSTALMENT_TYPE = 'future_instalment'

const ProgramRupees = ({ summary }) => {
  const paymentSummary = summary || []

  // The last item in the array contains the total amount
  const summaryItems = paymentSummary.slice(0, -1) || []
  const totalItem = paymentSummary[paymentSummary.length - 1]
  const totalAmount = totalItem?.total_amount || 0

  // Charmi #6 (2026-05-25) — total payable should appear before future
  // instalments. Future-instalment lines are emitted per-item by the
  // backend (Feature 1 breakdown); pull them out of the per-item flow
  // and render them in a single combined block AFTER total payable.
  const allFutureInstalments = summaryItems.flatMap((item) =>
    (item.line_items || []).filter((li) => li.type === FUTURE_INSTALMENT_TYPE)
  )

  return (
    <>
      <Styles.ProgramContentContainer>
        <Styles.Subheading>payment summary</Styles.Subheading>
        <Styles.PaymentSummary>
          {summaryItems.map((item, index) => {
            // Formatting display values
            const itemLetter = String.fromCharCode(97 + index) // a, b, c, etc.
            const lineItems = item.line_items || []

            // Feature 1 breakdown lines (current-term portion, optional
            // next-term portion) render inline as informational lines.
            // Future-instalment lines are pulled out (see allFutureInstalments
            // above) and rendered after the cart-wide total payable.
            const renderableLineItems = lineItems.filter(
              (li) => li.type !== FUTURE_INSTALMENT_TYPE
            )

            return (
              <React.Fragment key={item.id || index}>
                {renderableLineItems.map((lineItem, lineIdx) => {
                  if (lineItem.type === 'final_total') return null

                  // Apply different styles based on line item type
                  const isDiscount = lineItem.type === 'discount'
                  const isSubtraction = lineItem.operation === 'subtraction'
                  const isTotal = lineItem.type === 'total'

                  const textColor = isSubtraction ? '#EF5C34' : 'inherit'

                  const isDotted =
                    (isDiscount || isTotal) &&
                    renderableLineItems?.[lineIdx + 1]?.type != 'final_total' // final total already has a dotted top border

                  const fontWeight = isTotal ? 500 : 400
                  return (
                    <React.Fragment key={`line-${lineIdx}`}>
                      <Styles.SummaryItem>
                        <Styles.LineEntryTextSpan
                          fontWeight={fontWeight}
                          color={textColor}>
                          {lineItem.label}
                        </Styles.LineEntryTextSpan>

                        <Styles.LineEntryTextSpan
                          fontWeight={fontWeight}
                          color={textColor}>
                          {isDiscount ? '-' : ''}
                          {formatAmountIndian(lineItem.value || 0)}
                        </Styles.LineEntryTextSpan>
                      </Styles.SummaryItem>

                      {isDotted && <Styles.DottedSeparater />}
                    </React.Fragment>
                  )
                })}

                {/* Item total (grand total for this item) */}
                {renderableLineItems.find((item) => item.type === 'final_total') && (
                  <Styles.TotalPayable>
                    <span>item total ({itemLetter})</span>
                    <span>
                      {formatAmountIndian(
                        renderableLineItems.find((item) => item.type === 'final_total')
                          ?.value || 0
                      )}
                    </span>
                  </Styles.TotalPayable>
                )}

                {index < summaryItems.length - 1 && <Styles.Separator />}
              </React.Fragment>
            )
          })}

          {/* Grand total for all items combined — placed before future
              instalments per Charmi #6 (2026-05-25). */}
          {summaryItems.length > 0 && (
            <>
              <Styles.Separator />
              <Styles.TotalContainer>
                <span>
                  total payable (
                  {summaryItems
                    .map((_, i) => String.fromCharCode(97 + i))
                    .join('+')}
                  )
                </span>
                <span>{formatAmountIndian(totalAmount)}</span>
              </Styles.TotalContainer>
            </>
          )}

          {/* Future-instalment preview block (combined across all items).
              Backend emits one line per PENDING term so ops can see the
              full payment ladder upfront. */}
          {allFutureInstalments.length > 0 && (
            <>
              <Styles.DottedSeparater />
              <Styles.SummaryItem>
                <Styles.LineEntryTextSpan fontWeight={500}>
                  future instalments
                </Styles.LineEntryTextSpan>
                <Styles.LineEntryTextSpan fontWeight={500}>
                  {' '}
                </Styles.LineEntryTextSpan>
              </Styles.SummaryItem>
              {allFutureInstalments.map((futureItem, fIdx) => (
                <Styles.SummaryItem key={`future-${fIdx}`}>
                  <Styles.LineEntryTextSpan fontWeight={400}>
                    {futureItem.label}
                  </Styles.LineEntryTextSpan>
                  <Styles.LineEntryTextSpan fontWeight={400}>
                    {formatAmountIndian(futureItem.value || 0)}
                  </Styles.LineEntryTextSpan>
                </Styles.SummaryItem>
              ))}
            </>
          )}
        </Styles.PaymentSummary>
      </Styles.ProgramContentContainer>
    </>
  )
}

export default ProgramRupees
